import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../navbar';
import Footer from '../footer';
import { MapPin, ArrowRight, Loader2, Search, Lock, Mail, Phone, ShieldCheck } from 'lucide-react';
import service from '../../Service/Service';
import { trackEvent, trackFlightSearch } from '../../utils/analytics';
import PhoneCountryInput from '../Home3/PhoneCountryInput';
import { isValidContactPhone, mergeSavedContactIntoBooking, saveFlightSearchContact, submitRouteInquiryIfReady } from '../../utils/flightSearchDetails';
import PriceDisplay from "../PriceDisplay";

const TRUST_MARKERS = ['No spam', 'Free', 'Secure'];

const buildBookingData = (deal) => ({
  departure: deal.origin_code,
  arrival: deal.destination_code,
  departure_name: `${deal.origin_city} (${deal.origin_code})`,
  arrival_name: `${deal.destination_city} (${deal.destination_code})`,
  date: deal.departure_date || '',
  return_date: deal.return_date || '',
  name: '',
  phone: '',
  adults: '1',
  children: '0',
  infants: '0',
  flight_type: deal.return_date ? 'return' : 'one-way',
  flight_class: 'economy'
});

const buildBookingSearchParams = (deal, formState) => ({
  departure: deal.origin_code,
  arrival: deal.destination_code,
  departure_name: `${deal.origin_city} (${deal.origin_code})`,
  arrival_name: `${deal.destination_city} (${deal.destination_code})`,
  date: deal.departure_date || '',
  return_date: deal.return_date || '',
  full_name: formState.fullName.trim(),
  phone: formState.phone.trim(),
  email: formState.email.trim(),
  adult: 1,
  children: 0,
  infants: 0,
  status: 'SEARCH',
  reviews: '',
  description: `${deal.return_date ? 'Round Trip' : 'One Way'} - economy`,
  flight_type: deal.return_date ? 'return' : 'one-way',
  flight_class: 'economy'
});

const FlightDealLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState(null);
  const [formState, setFormState] = useState({
    fullName: '',
    phone: '',
    email: '',
  });
  const [submitError, setSubmitError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  console.log('✅ FlightDealLanding component loaded! Slug:', slug);

  useEffect(() => {
    const fetchDealAndRedirect = async () => {
      if (!slug) {
        navigate('/');
        return;
      }

      try {
        // Fetch deal by slug
        console.log('🔍 Fetching deal with slug:', slug);
        const response = await service.getDealBySlug(slug);
        console.log('📦 API Response:', response);
        
        if (response.data && response.data.success && response.data.data) {
          const dealData = response.data.data;
          console.log('✅ Deal found:', dealData);
          
          // Prepare booking data from deal + saved guest phone
          const bookingData = mergeSavedContactIntoBooking(buildBookingData(dealData));

          // Store booking data in localStorage
          localStorage.setItem('booking', JSON.stringify(bookingData));
          localStorage.removeItem('offer'); // Clear cached offers to force fresh search

          setDeal(dealData);
          if (bookingData.phone) {
            setFormState((current) => ({
              ...current,
              fullName: bookingData.name || current.fullName,
              phone: bookingData.phone || current.phone,
              email: bookingData.email || current.email,
            }));
            // Create Available Inquiry with origin/destination once guest phone exists
            submitRouteInquiryIfReady(bookingData).catch(() => {});
          }
          trackEvent('deal_page_view', {
            source: 'deal_landing',
            slug,
            origin: dealData.origin_code,
            destination: dealData.destination_code,
            route: `${dealData.origin_code}-${dealData.destination_code}`,
          });
          
          // Small delay to ensure SEO content is rendered for crawlers
          setTimeout(() => {
            setLoading(false);
            // Don't auto-redirect - show button instead
          }, 1500);
        } else {
          console.warn('⚠️ Deal not found or inactive. Response:', response);
          setError('Deal not found. Please check that the deal exists and is active.');
          setTimeout(() => navigate('/'), 3000);
        }
      } catch (err) {
        console.error('❌ Error fetching deal:', err);
        console.error('Error details:', err.response?.data || err.message);
        setError(`Error: ${err.response?.data?.error || err.message || 'Deal not found'}`);
        setTimeout(() => navigate('/'), 3000);
      }
    };

    fetchDealAndRedirect();
  }, [slug, navigate]);

  const handleFieldChange = (field) => (event) => {
    setFormState((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handlePhoneChange = (fullPhone) => {
    setFormState((current) => ({
      ...current,
      phone: fullPhone,
    }));
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    if (!deal) return;

    const trimmedName = formState.fullName.trim();
    const trimmedPhone = formState.phone.trim();
    const trimmedEmail = formState.email.trim();

    if (!trimmedName) {
      setSubmitError('Please enter your full name.');
      return;
    }

    if (!trimmedPhone || !isValidContactPhone(trimmedPhone)) {
      setSubmitError('Please enter a valid phone number.');
      return;
    }

    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setSubmitError('Please enter a valid email address.');
      return;
    }

    setSubmitError('');
    setSubmitLoading(true);

    const bookingData = {
      ...buildBookingData(deal),
      name: trimmedName || 'Guest',
      phone: trimmedPhone,
      email: trimmedEmail,
    };

    saveFlightSearchContact({
      name: bookingData.name,
      phone: bookingData.phone,
      email: bookingData.email,
    });
    localStorage.setItem('booking', JSON.stringify(bookingData));
    localStorage.removeItem('offer');

    submitRouteInquiryIfReady(bookingData).catch(() => {});

    const bookingSearchParams = buildBookingSearchParams(deal, {
      ...formState,
      fullName: bookingData.name,
      phone: bookingData.phone,
      email: bookingData.email,
    });

    service
      .addBooking(bookingSearchParams)
      .then((bookingResponse) => {
        if (bookingResponse?.data?.insertId) {
          localStorage.setItem('bookingresult', JSON.stringify(bookingResponse.data));
        }
      })
      .catch((saveError) => {
        console.error('Error saving deal inquiry:', saveError);
      });

    trackEvent('deal_form_submit', {
      source: 'deal_landing',
      slug,
      origin: deal.origin_code,
      destination: deal.destination_code,
      route: `${deal.origin_code}-${deal.destination_code}`,
      has_email: trimmedEmail ? 'yes' : 'no',
    });
    trackFlightSearch({
      source: 'deal_landing',
      origin: deal.origin_code,
      destination: deal.destination_code,
      route: `${deal.origin_code}-${deal.destination_code}`,
      flight_type: bookingData.flight_type || 'return',
      flight_class: bookingData.flight_class || 'economy',
      has_return_date: bookingData.return_date ? 'yes' : 'no',
    });

    navigate('/flight-detail-search?package=0');
  };

  if (error) {
    return (
      <>
        <Helmet>
          <title>Deal Not Found | SKYSALE PK - Cheap Flights</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Deal Not Found</h2>
            <p className="text-gray-600 mb-2">{error}</p>
            <p className="text-sm text-gray-500">Redirecting to homepage...</p>
            <p className="text-xs text-gray-400 mt-4">Slug: {slug}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = deal 
    ? `Cheap Flights from ${deal.origin_city} to ${deal.destination_city} | Best Deals & Prices`
    : 'Cheap Flights | Best Deals & Prices';
  const pageDescription = deal
    ? `Find the cheapest flights from ${deal.origin_city} to ${deal.destination_city}. Compare prices from multiple airlines and book your next trip with the best deals available.`
    : 'Find the cheapest flights to destinations worldwide. Compare prices and book with confidence.';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`cheap flights, ${deal?.origin_city} to ${deal?.destination_city}, flight deals, airline tickets, ${deal?.origin_city} flights, ${deal?.destination_city} flights`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={window.location.href} />
        {deal?.image_path && (
          <meta property="og:image" content={`${service.baseURL}/uploads/${deal.image_path}`} />
        )}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Navbar bg="blue" />
        
        {/* Header Section with Blue Background */}
        {deal && (
          <div className="bg-secondary text-white py-12 md:py-16 mt-14 relative pb-20 md:pb-24">
            <div className="max-w-7xl mx-auto px-4">
              <div className="text-center">
                <h1 className="text-3xl md:text-5xl font-bold mb-4">
                  Cheap Flights from {deal.origin_city} to {deal.destination_city}
                </h1>
                <p className="text-lg md:text-xl text-navy-100 mb-6">
                  Find the best flight deals from {deal.origin_city} to {deal.destination_city}. Compare prices from top airlines and save on your next trip.
                </p>
                
                {/* Route Information */}
                <div className="flex items-center justify-center gap-4 md:gap-8 mb-6">
                  <div className="text-center">
                    <MapPin className="w-8 h-8 md:w-10 md:h-10 mx-auto mb-2 text-navy-200" />
                    <p className="font-semibold text-lg md:text-xl">{deal.origin_city}</p>
                    <p className="text-sm md:text-base text-navy-200">({deal.origin_code})</p>
                  </div>
                  <ArrowRight className="w-6 h-6 md:w-8 md:h-8 text-navy-200" />
                  <div className="text-center">
                    <MapPin className="w-8 h-8 md:w-10 md:h-10 mx-auto mb-2 text-navy-200" />
                    <p className="font-semibold text-lg md:text-xl">{deal.destination_city}</p>
                    <p className="text-sm md:text-base text-navy-200">({deal.destination_code})</p>
                  </div>
                </div>
                
                {/* Price Display */}
                {deal.cached_price && (
                  <div className="text-center">
                    <p className="text-sm md:text-base text-navy-200 mb-1">Starting from</p>
                    <p className="text-4xl md:text-5xl font-bold text-white">
                      <PriceDisplay amount={parseFloat(deal.cached_price)} />
                    </p>
                  </div>
                )}
              </div>
            </div>
            
          </div>
        )}

        {!loading && deal && (
          <div className="max-w-7xl mx-auto px-4 -mt-8 md:-mt-12 relative z-10">
            <div className="mx-auto max-w-5xl overflow-hidden rounded-[28px] border border-secondary/15 bg-white shadow-[0_20px_60px_rgba(35,25,74,0.18)]">
              <div className="flex flex-col gap-4 bg-secondary px-6 py-5 text-white md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-1 rounded-xl bg-white/10 p-3">
                    <Lock className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Unlock This Deal</h2>
                    <p className="text-sm text-navy-100 md:text-base">
                      Enter your details to see available flights for this route.
                    </p>
                  </div>
                </div>
                {deal.cached_price && (
                  <div className="inline-flex w-fit items-center rounded-full bg-[#ffcf40] px-4 py-2 text-sm font-bold text-[#5a3900] shadow-sm">
                    from <PriceDisplay amount={parseFloat(deal.cached_price)} />
                  </div>
                )}
              </div>

              <form className="px-6 py-6 md:px-8 md:py-7" onSubmit={handleSearchSubmit}>
                <div className="grid gap-4 md:grid-cols-[1.3fr_1.4fr_1.2fr]">
                  <label className="block">
                    <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
                      Full Name <span className="text-red-500">*</span>
                    </span>
                    <input
                      type="text"
                      value={formState.fullName}
                      onChange={handleFieldChange('fullName')}
                      placeholder="e.g. John Smith"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 outline-none transition focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                    />
                  </label>

                  <div className="block">
                    <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
                      <Phone className="h-4 w-4 text-secondary" />
                      Phone Number <span className="text-red-500">*</span>
                    </span>
                    <PhoneCountryInput
                      id="deal-landing-phone"
                      value={formState.phone}
                      onChange={handlePhoneChange}
                      disabled={submitLoading}
                    />
                  </div>

                  <label className="block">
                    <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
                      <Mail className="h-4 w-4 text-secondary" />
                      Email <span className="text-gray-400">(optional)</span>
                    </span>
                    <input
                      type="email"
                      value={formState.email}
                      onChange={handleFieldChange('email')}
                      placeholder="your@email.com"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 outline-none transition focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                    />
                  </label>
                </div>

                {submitError && (
                  <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {submitError}
                  </div>
                )}

                <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <button
                      type="submit"
                      disabled={submitLoading}
                      className="inline-flex items-center justify-center gap-3 rounded-xl bg-secondary px-6 py-3.5 text-base font-bold text-white transition hover:bg-navy-800 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      <Search className="h-5 w-5" />
                      {submitLoading ? 'Checking...' : 'Check Availability'}
                    </button>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      {TRUST_MARKERS.map((item) => (
                        <span key={item} className="inline-flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 text-secondary" />
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* SEO Content Section */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8 pt-12 md:pt-14">

            {/* Loading State */}
            {loading && (
              <div className="text-center py-8">
                <div className="inline-block">
                  <Loader2 className="w-12 h-12 text-secondary animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading best flight deals...</p>
                </div>
              </div>
            )}

            {/* SEO Content */}
            <div className="prose max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {deal ? `Why Book Flights from ${deal.origin_city} to ${deal.destination_city}?` : 'Why Book With Us?'}
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Compare prices from multiple airlines in one place</li>
                <li>Best price guarantee - we search both Duffel and Amadeus APIs</li>
                <li>Easy booking process with secure payment</li>
                <li>24/7 customer support</li>
                <li>Flexible booking options for one-way and round-trip flights</li>
                <li>No hidden fees - transparent pricing</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                How to Find the Best Flight Deals
              </h2>
              <p className="text-gray-700 mb-4">
                {deal 
                  ? `When searching for flights from ${deal.origin_city} to ${deal.destination_city}, we recommend booking in advance for the best prices. Our system searches multiple airline providers to ensure you get the best deal available.`
                  : 'Our advanced search system compares prices from multiple airlines and booking platforms to find you the best deals. Simply enter your travel dates and we\'ll show you all available options.'
                }
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                Popular Flight Routes
              </h2>
              <p className="text-gray-700">
                We offer competitive prices on flights to popular destinations worldwide. Whether you're planning a business trip or a vacation, we have deals for you.
              </p>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default FlightDealLanding;
